import { format, isValid } from 'date-fns';

import { EXTERNAL_URLS } from '@zg-rentals/zillow-rental-manager/constants';

import { getDaysLeft } from '../../hooks/useIdentityVerification';

import type { IdentityVerification } from '../../hooks/useIdentityVerification';
import type { IdentityVerificationRestrictedAction } from '../../types/identityVerification';

// Placeholder until the BE exposes its renewal window setting.
const RENEWAL_WINDOW_DAYS = 30;

const ACTION_PHRASES: Record<IdentityVerificationRestrictedAction, string> = {
  PUBLISH_LISTING: 'publish listings',
  INVITE_TO_APPLY: 'invite renters to apply',
  SETUP_PAYMENTS: 'set up payments',
};

const KEEP_PUBLISHING = 'keep publishing listings';

const toActionPhrase = (actions: Array<IdentityVerificationRestrictedAction>) => {
  const phrases = actions.map((action) => ACTION_PHRASES[action]);
  if (phrases.length <= 1) return phrases[0];
  return `${phrases.slice(0, -1).join(', ')} and ${phrases.at(-1)}`;
};

export const formatVerificationDate = (timestamp: number) =>
  isValid(timestamp) ? format(timestamp, 'MMMM d, yyyy') : undefined;

export type IdentityVerificationContent = {
  tone: 'info' | 'warning' | 'critical';
  header: string;
  body: string;
  /** Without `href`, the CTA opens the verify identity page. */
  cta?: { label: string; href?: string };
  isDismissible?: boolean;
};

const verifyCta = (label: string) => ({ label });

export const getBannerContent = (
  { entry, restrictedActions, isEnforced, daysLeft }: IdentityVerification,
  now: number,
): IdentityVerificationContent | undefined => {
  if (!entry) return undefined;

  const actions = toActionPhrase(restrictedActions);

  switch (entry.status) {
    case 'NOT_STARTED':
      if (isEnforced) {
        return {
          tone: 'warning',
          header: 'Verify your identity',
          body: `Required to ${actions}`,
          cta: verifyCta('Verify now'),
        };
      }
      if (entry.enrollment && daysLeft !== undefined && daysLeft > 0) {
        const deadline = formatVerificationDate(entry.enrollment.gracePeriodEndsAt);
        if (!deadline) return undefined;
        return {
          tone: 'info',
          header: 'Verify your identity',
          body: `You can keep using all features for now. Verify by ${deadline} to avoid interruptions when publishing a new listing or reactivating an off-market listing.`,
          cta: verifyCta('Verify now'),
        };
      }
      return undefined;
    case 'IN_PROGRESS':
      return {
        tone: 'info',
        header: 'Finish verifying your identity',
        body: 'You started verifying your identity. Pick up where you left off.',
        cta: verifyCta('Continue verifying'),
      };
    case 'NEEDS_REVIEW':
      return {
        tone: 'info',
        header: 'Your identity verification is under review',
        body: `We’ll email you when it’s done${actions ? `. You can’t ${actions} until then.` : ''}`,
        isDismissible: true,
      };
    case 'FAILED':
      return {
        tone: 'critical',
        header: 'We couldn’t verify your identity',
        body: 'Contact support if you think this is a mistake',
        cta: { label: 'Contact support', href: EXTERNAL_URLS.SUPPORT_EMAIL },
      };
    case 'REVOKED':
      return {
        tone: 'critical',
        header: 'Verify your identity again',
        body: `Your identity verification is no longer valid. Verify again to ${actions ?? KEEP_PUBLISHING}.`,
        cta: verifyCta('Verify again'),
      };
    case 'EXPIRED':
      return {
        tone: 'warning',
        header: 'Your identity verification expired',
        body: `Verify again to ${actions ?? KEEP_PUBLISHING}`,
        cta: verifyCta('Verify again'),
      };
    case 'VERIFIED': {
      const { expiresAt } = entry;
      if (expiresAt === undefined || getDaysLeft(expiresAt, now) > RENEWAL_WINDOW_DAYS) return undefined;
      const expiresOn = formatVerificationDate(expiresAt);
      if (!expiresOn) return undefined;
      return {
        tone: 'info',
        header: 'Renew your identity verification',
        body: `Your verification expires on ${expiresOn}. Renew it to ${KEEP_PUBLISHING}.`,
        cta: verifyCta('Renew'),
        isDismissible: true,
      };
    }
  }
};
